import { ChangeDetectionStrategy, Component, Input, Type } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { WithUuid } from '@nk/data-entities-lib';
import { OrderBy, SortDirection } from '@nk/utils-lib';
import { BehaviorSubject } from 'rxjs';

export interface NkPagination {
  limit: number;
  page: number;
  totalCount: number;
}

export type EntityWithUuid = WithUuid<{ uuid?: string }>;

export interface NkCardListData<Entity extends EntityWithUuid> {
  entities: Entity[];
  pagination: NkPagination;
  sorts?: CardListSorts<Entity>;
  highlightText?: string;
}

export interface NkCardListConfig<Entity extends EntityWithUuid> {
  items: Array<NkCardListItemConfig<Entity, Type<NkCardListItem<Entity>>>>;
  detailsComponent?: Type<NkCardListItemDetails<Entity>>;
  noDataMessage: string | undefined;
}

/**
 * Extracts the prop type from a component that extends NkCardListItemAbstractComponent.
 * If the component doesn't specify a specific prop (uses default), returns keyof Entity.
 */
type ExtractPropFromComponent<Component extends Type<unknown>, Entity extends EntityWithUuid> =
  Component extends Type<infer Instance>
    ? Instance extends NkCardListItemAbstractComponent<Entity, infer K>
      ? K
      : keyof Entity
    : keyof Entity;

/**
 * Type-safe card list item config that enforces prop matches component expectations.
 * When a component extends NkCardListItemAbstractComponent<Entity, 'status'>,
 * the prop must be 'status'.
 */
export type NkCardListItemConfig<
  Entity extends EntityWithUuid,
  Component extends Type<NkCardListItem<Entity>> = Type<NkCardListItem<Entity>>,
> = {
  label: string;
  prop: ExtractPropFromComponent<Component, Entity>;
  andClass: string;
  component: Component;
  sortable: boolean;
  headerClass?: string;
  headerTooltipMessage?: string;
};

/**
 * Helper function to create a type-safe card list item config.
 * This ensures that when a component expects a specific prop (e.g., 'status'),
 * the prop field must match that expectation.
 *
 * @example
 * ```typescript
 * createCardListItemConfig<MyEntity, typeof MyStatusComponent>({
 *   prop: 'status',
 *   component: MyStatusComponent, // expects 'status'
 *   label: 'Status',
 *   andClass: 'col-sm-2',
 *   sortable: false,
 * })
 * ```
 */
export function createCardListItemConfig<Entity extends EntityWithUuid, Component extends Type<NkCardListItem<Entity>>>(
  config: Component extends Type<infer Instance>
    ? Instance extends NkCardListItemAbstractComponent<Entity, infer K>
      ? K extends keyof Entity
        ? Omit<NkCardListItemConfig<Entity, Component>, 'component' | 'prop'> & {
            prop: K;
            component: Component;
          }
        : never
      : Omit<NkCardListItemConfig<Entity, Component>, 'component' | 'prop'> & {
          prop: ExtractPropFromComponent<Component, Entity>;
          component: Component;
        }
    : never,
): NkCardListItemConfig<Entity> {
  return config as NkCardListItemConfig<Entity>;
}

/**
 * Helper function to create a type-safe card list config.
 * This validates that each item's prop matches its component's expected prop.
 * TypeScript will show an error if any prop doesn't match its component's expected prop.
 *
 * For best type safety, use createCardListItemConfig for each item:
 *
 * @example
 * ```typescript
 * export const MY_CONFIG: NkCardListConfig<MyEntity> = {
 *   items: [
 *     createCardListItemConfig<MyEntity, typeof MyStatusComponent>({
 *       prop: 'status',
 *       component: MyStatusComponent,
 *       ...
 *     }),
 *   ],
 *   ...
 * };
 * ```
 */
export function createCardListConfig<Entity extends EntityWithUuid>(
  config: NkCardListConfig<Entity>,
): NkCardListConfig<Entity> {
  return config;
}

export interface NkCardListItem<Entity extends EntityWithUuid> {
  entity: Entity | undefined;
  prop: keyof Entity | undefined;
  highlightText?: string;
}

export interface NkCardListItemDetails<Entity extends EntityWithUuid> {
  entity: Entity;
  highlightText?: string;
}

export interface NkCardListItemLink {
  routerLink: string[] | null;
  label: string;
  subLabel?: string;
  tag?: string;
}

export interface NkCardListItemTag {
  label: string;
  color: string;
}

/*
 * Each CardListItem component for the card list should extend this abstract one.
 *
 * The reason is when we dynamically create a component and insert it into the view,
 * Angular will invoke each one of the lifecycle hooks except for the ngOnChanges() hook.
 *
 * @template T - The entity type (e.g., ScreeningCaseCardListItem)
 * @template K - The specific prop key this component handles (e.g., 'status')
 *
 * Example:
 * ```typescript
 * export class MyComponent extends NkCardListItemAbstractComponent<MyEntity, 'status'> {
 *   // prop is typed as 'status' | undefined
 *   // entity[prop] is typed as MyEntity['status'] | undefined
 * }
 * ```
 */
@Component({ template: '', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush })
export abstract class NkCardListItemAbstractComponent<T, K extends keyof T = keyof T> {
  @Input() public highlightText?: string;

  @Input() public set entity(entity: T | undefined) {
    this._entity = entity;
    this._updateValue();
  }
  public get entity(): T | undefined {
    return this._entity;
  }

  @Input() public set prop(prop: K | undefined) {
    this._prop = prop;
    this._updateValue();
  }
  public get prop(): K | undefined {
    return this._prop;
  }

  private _entity: T | undefined;
  private _prop: K | undefined;

  private _value = new BehaviorSubject<{ entity: T; prop: K } | null>(null);
  /**
   * @see use {@link valueSignal} when you do not need async things
   */
  protected value$ = this._value.asObservable();

  /**
   * @see use {@link value$} when you need async things
   */
  protected valueSignal = toSignal(this.value$);

  private _updateValue(): void {
    if (this._entity && this._prop) {
      this._value.next({
        entity: this._entity,
        prop: this._prop,
      });
    }
  }
}

export type CardListSorts<Entity extends EntityWithUuid> = Partial<Record<keyof Entity, SortDirection>>;

const SORT_DIRECTION_ORDER: SortDirection[] = [undefined, 'desc', 'asc'];

export function getNextSortDirection<Entity extends EntityWithUuid>(
  sorts: CardListSorts<Entity>,
  item: NkCardListItemConfig<Entity>,
): SortDirection {
  let index = SORT_DIRECTION_ORDER.findIndex((dir) => dir === sorts[item.prop]) || 0;
  index++;
  if (index === SORT_DIRECTION_ORDER.length) {
    index = 0;
  }
  return SORT_DIRECTION_ORDER[index];
}

export function orderByToCardListSorts<
  Entity extends EntityWithUuid,
  Mapping extends Record<string, string> = Record<string, string>,
>(orderBy: OrderBy[], reversedOrderByMapping: Mapping): CardListSorts<Entity> {
  return (orderBy || []).reduce((res: CardListSorts<Entity>, item) => {
    const [sortParamKey, sortDirection] = item.split(':');
    if (reversedOrderByMapping[sortParamKey]) {
      res[reversedOrderByMapping[sortParamKey] as keyof Entity] = sortDirection as SortDirection;
    }
    return res;
  }, {});
}
